/**
 * PATH: packages/ui/src/components/mobile-dock/useDockFits.ts
 * FILE_ID: UI-MOBILEDOCK-FITS-HOOK-001
 * PURPOSE: Does every dock tile fit across the strip with no scrolling? Drives
 *          the opt-in compact chrome (no move handle, no hide control) for a
 *          short menu such as a field worker's four items.
 * LANDMINE: measure the scroller's content against the WHOLE dock width, not
 *          the scroller's own width. Hiding the chrome widens the scroller,
 *          and a self-relative test would flip back and forth.
 * RELATED: ./MobileDock.tsx
 */
import { useEffect, useState, type RefObject } from 'react'

/** Pure: content width vs the room the whole strip offers. */
export function tilesFit(contentWidth: number, dockWidth: number): boolean {
  return dockWidth > 0 && contentWidth <= dockWidth
}

export function useDockFits(
  scrollRef: RefObject<HTMLElement | null>,
  dockRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  itemCount: number,
): boolean {
  const [fits, setFits] = useState(false)
  useEffect(() => {
    if (!enabled) {
      setFits(false)
      return undefined
    }
    const measure = () => {
      const scroller = scrollRef.current
      const dock = dockRef.current
      setFits(!!scroller && !!dock && tilesFit(scroller.scrollWidth, dock.clientWidth))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [enabled, itemCount, scrollRef, dockRef])
  return fits
}
